// src/components/ClaveForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, endpoints } from '../api/api';
import { t } from '../components/i18n';
import logo from '../assets/LIR-BLANCO.png';
import compassLogo from '../assets/Recurso-8compass-NEGRO.png';
import LangSwitcher from '../components/LangSwitcher';

const ClaveForm = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Idioma derivado del slash (/en o /es)
  const effectiveLang = location.pathname.startsWith('/en') ? 'en' : 'es';
  const dict = (t && t[effectiveLang]) ? t[effectiveLang] : (t?.es || {});

  const [clave, setClave] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 600;
      setIsMobile(mobile);
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = async () => {
    const claveTrim = clave.trim();
    if (!claveTrim) {
      setError(dict.error_empty_key || 'Ingresa tu clave.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post(endpoints.loginClave(), {
        clave: claveTrim,
      });

      if (!data || typeof data !== 'object' || !data.id) {
        throw new Error('PAYLOAD_INVALID');
      }

      // Guardamos también la clave junto al sujeto
      const sujetoWithClave = { ...(data || {}), clave: claveTrim };
      // Verifica artículos asignados
      let asignados = [];
      try {
        const r = await api.get(endpoints.articulos(sujetoWithClave.id));
        asignados = Array.isArray(r.data) ? r.data : [];
      } catch (err) {
        console.error('Error fetching assigned articles', err);
        throw new Error('ASSIGNED_FETCH_FAILED');
      }
      const sujetoFull = {
        ...sujetoWithClave,
        tieneArticulos: asignados.length > 0,
      };

      // Propaga al padre y persiste para rehidratación tras refresh
      onLogin?.(sujetoFull);

      try {
        localStorage.setItem('lir_sujeto', JSON.stringify(sujetoFull));
      } catch (err) {
        console.error('Error storing subject in localStorage', err);
        throw new Error('STORAGE_WRITE_FAILED');
      }

      try {
        sessionStorage.removeItem('lir_submitted');
      } catch (err) {
        console.error('Error clearing submitted flag', err);
        throw new Error('STORAGE_WRITE_FAILED');
      }

      // Navega respetando el idioma del slash
      const next = sujetoFull.tieneArticulos || !sujetoFull.IH_Pro
        ? (effectiveLang === 'en' ? '/en/articles' : '/es/articulos')
        : (effectiveLang === 'en' ? '/en/select-articles' : '/es/seleccionar-articulos');
      navigate(next, { replace: true });
    } catch (err) {
      const baseMessage = dict.error_invalid_key || 'Clave no válida o no encontrada.';

      let code = '06';
      if (err?.response?.status >= 500) {
        code = '02';
      } else if (err?.response?.status === 404 || err?.response?.status === 401 || err?.response?.status === 400) {
        code = '00';
      } else if (!err?.response && err?.request) {
        code = '01';
      } else if (err?.message === 'PAYLOAD_INVALID') {
        code = '03';
      } else if (err?.message === 'STORAGE_WRITE_FAILED') {
        code = '04';
      } else if (err?.message === 'ASSIGNED_FETCH_FAILED') {
        code = '05';
      }

      setError(`${baseMessage} (error ${code})`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(1200px 800px at 20% -10%, rgba(233,63,99,0.15) 0%, rgba(18,18,18,0) 40%), radial-gradient(900px 700px at 120% 120%, rgba(255,255,255,0.06) 0%, rgba(18,18,18,0) 35%), #121212',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: isMobile ? 'flex-start' : 'center',
        position: 'relative',
        padding: `${headerHeight + 32}px 1rem 3rem`,
        gap: isMobile ? '2rem' : undefined,
        color: '#fff',
        fontFamily: 'Segoe UI, sans-serif',
      }}    >
        
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          position: 'absolute',
          top: isMobile ? 16 : 24,
          left: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          gap: '1rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexDirection: isMobile ? 'column' : 'row',
            textAlign: isMobile ? 'center' : 'left',
            width: '100%',
          }}
        >
          <img
            src={logo}
            alt="LIR"
            style={{ height: isMobile ? 40 : 48, objectFit: 'contain' }}
          />
          <div>
            <h1
              style={{
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                margin: 0,
                fontWeight: 800,
                letterSpacing: '.2px',
              }}
            >
              {dict.hero_title || 'LIR Council: evaluación exclusiva de artículos legales'}
            </h1>
            <p
              style={{
                color: '#bdbdbd',
                margin: 0,
                fontSize: isMobile ? '.9rem' : '1rem',
              }}
            >
              {dict.hero_sub || 'Plataforma privada de retroalimentación profesional'}
            </p>
          </div>
        </div>

        {/* Selector de idioma + logo derecha */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '.6rem',
            alignSelf: isMobile ? 'flex-end' : 'auto',
            marginTop: isMobile ? '0.5rem' : 0,
          }}
        >
          <LangSwitcher />
          <img
            src={compassLogo}
            alt="Compass"
            style={{ height: isMobile ? 40 : 44, objectFit: 'contain', opacity: 0.95 }}
          />
        </div>
      </div>

      {/* Burbuja de agradecimiento */}
      <div style={{ width: '100%', maxWidth: 760, marginBottom: isMobile ? '2rem' : '1rem' }}>
        <div
          style={{
            background: 'rgba(22,22,22,.92)',
            border: '1px solid #2a2a2a',
            borderRadius: 14,
            padding: '0.95rem 1.1rem',
            boxShadow: '0 10px 24px rgba(0,0,0,.35)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 4 }}>
            {dict.member_thanks_title || 'Muchas gracias por ser parte de LIR Council'}
          </div>
          <div style={{ color: '#cfcfcf', fontSize: '.95rem', lineHeight: 1.5 }}>
            {dict.member_thanks_body ||
              'En esta plataforma, encontrarás los próximos artículos a evaluar. Para entrar, por favor ingresa la clave única que está en tu correo.'}
          </div>
        </div>
      </div>

      {/* Card */}
      <div
        style={{
          padding: isMobile ? '1.5rem' : '2rem',
          backgroundColor: 'rgba(30,30,30,.9)',
          borderRadius: 14,
          maxWidth: 460,
          width: '100%',
          border: '1px solid #2a2a2a',
          boxShadow: '0 10px 30px rgba(0,0,0,.45)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontWeight: 800,
            fontSize: isMobile ? '1.4rem' : '1.6rem',
          }}
        >
          {dict.enter_key || 'Ingrese su clave'}
        </h2>

        <p
          style={{
            color: '#a8a8a8',
            margin: 0,
            fontSize: '.95rem',
          }}
        >
          {dict.check_email || 'Revisa tu correo para la clave única de acceso.'}
        </p>

        <label htmlFor="clave" style={{ display: 'none' }}>
          {dict.key_placeholder || 'Clave recibida por correo'}
        </label>

        <div style={{ position: 'relative', textAlign: 'left' }}>
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.7,
            }}
          >
            🔒
          </span>

          <input
            id="clave"
            type="text"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={dict.key_placeholder || 'Clave recibida por correo'}
            aria-label={dict.key_placeholder || 'Clave recibida por correo'}
            disabled={loading}
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: '0.85rem 0.9rem 0.85rem 2.2rem',
              borderRadius: 10,
              border: '1px solid #373737',
              backgroundColor: '#1c1c1c',
              color: '#ffffff',
              fontSize: '1rem',
              outline: 'none',
              transition: 'box-shadow .2s ease, border-color .2s ease',
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = '0 0 0 2px rgba(233,63,99,.3)';
              e.currentTarget.style.borderColor = '#E93F63';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#373737';
            }}
          />
        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            backgroundColor: '#E93F63',
            color: 'white',
            border: 'none',
            padding: '0.9rem 1.2rem',
            borderRadius: 10,
            fontWeight: 700,
            width: '100%',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            transition: 'background-color .25s ease, transform .1s ease',
            boxShadow: '0 6px 16px rgba(233,63,99,.35)',
          }}
          onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#ff3e6c')}
          onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#E93F63')}
          onMouseDown={(e) => !loading && (e.currentTarget.style.transform = 'translateY(1px)')}
          onMouseUp={(e) => !loading && (e.currentTarget.style.transform = 'translateY(0)')}
        >
          {loading ? (dict.loading || 'Accediendo…') : (dict.access || 'Acceder')}
        </button>

        {error && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              marginTop: 0,
              padding: '.75rem 1rem',
              background: 'rgba(255,76,76,.12)',
              border: '1px solid rgba(255,76,76,.3)',
              color: '#ff6b6b',
              borderRadius: 10,
              animation: 'fadeIn .25s ease',
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* CSS para la animación (sintaxis válida en JSX) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ClaveForm;
